/**
 * Filesystem API Service
 *
 * 基于 allocator 服务的文件系统接口
 * 分为 home（用户维度）和 workspace（实例维度）两个命名空间
 *
 * 基于 allocator-openapi.json 规范
 */

import { createLogger } from '@/src/utils/logger'
import { ALLOCATOR_BASE_URL } from './agent'
import { getToken } from './client'
import { API_BASE } from './config'

const log = createLogger('FileUpload')

// ── 类型定义 ──

/** allocator readdir 返回的原始条目 */
export interface AllocatorDirEntry {
  name: string
  /** 递归模式下的完整相对路径（如 export/example.txt） */
  relativePath?: string
  isSymlink: boolean
  isFile: boolean
  isDirectory: boolean
}

/** 统一的 API 响应包装 */
interface AllocatorResponse<T> {
  code: number
  data?: T
  message?: string
  stack?: string
}

// ── Inflight 请求去重 ──

const _inflightMap = new Map<string, Promise<unknown>>()

function deduped<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const existing = _inflightMap.get(key) as Promise<T> | undefined
  if (existing) return existing
  const p = fn().finally(() => { _inflightMap.delete(key) })
  _inflightMap.set(key, p)
  return p
}

// ── 内部 fetch 工具 ──

async function allocatorPost<T>(endpoint: string, body: unknown): Promise<T> {
  const res = await fetch(`${ALLOCATOR_BASE_URL}/${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || `allocator 请求失败 (${res.status})`)
  }

  const json: AllocatorResponse<T> = await res.json()
  if (json.code !== 0) {
    throw new Error(json.message || `allocator 业务错误 (code=${json.code})`)
  }

  return json.data as T
}

// ── Workspace 文件系统（实例维度，id = instanceId） ──

/**
 * 读取 workspace 目录
 * POST /api/allocator/filesystem.workspace.readdir
 */
export function readWorkspaceDir(
  instanceId: string,
  path: string,
  recursive = false,
): Promise<AllocatorDirEntry[]> {
  const key = `ws:readdir:${instanceId}:${path}:${recursive}`
  return deduped(key, () =>
    allocatorPost<AllocatorDirEntry[]>('filesystem.workspace.readdir', {
      id: instanceId,
      path,
      recursive,
    }),
  )
}

/**
 * 读取 workspace 文件内容
 * POST /api/allocator/filesystem.workspace.readFile
 */
export async function readWorkspaceFile(
  instanceId: string,
  path: string,
): Promise<string> {
  return allocatorPost<string>('filesystem.workspace.readFile', {
    id: instanceId,
    path,
  })
}

/**
 * 上传文件到 workspace
 * POST /api/allocator/filesystem.workspace.upload
 */
export async function uploadWorkspaceFile(
  instanceId: string,
  path: string,
  fileId: string,
  deleteExisting = false,
): Promise<void> {
  await allocatorPost('filesystem.workspace.upload', {
    id: instanceId,
    path,
    fileId,
    deleteExisting,
  })
}

/**
 * 浏览器端上传文件到 workspace（一步式）
 * POST /api/agent/workspace/filesystem/{instanceId}/upload/files
 * FormData: 文件（filename 为目标路径）
 */
export async function uploadBrowserFileToWorkspace(
  instanceId: string,
  targetPath: string,
  file: File,
): Promise<void> {
  const uploadUrl = `${API_BASE}/api/agent/workspace/filesystem/${instanceId}/upload/files`
  log.info('上传文件', { name: file.name, size: file.size, type: file.type, targetPath, uploadUrl })

  const formData = new FormData()
  formData.append('file', file, targetPath)

  const headers: Record<string, string> = {}
  const token = getToken()
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(uploadUrl, {
    method: 'POST',
    headers,
    body: formData,
  })

  log.info('响应状态', res.status, res.statusText)

  if (!res.ok) {
    const text = await res.text()
    log.error('上传失败', text)
    throw new Error(text || `上传失败 (${res.status})`)
  }

  const json = await res.json()
  log.info('响应体', json)

  if (json.code !== 0) {
    log.error('业务错误', json)
    throw new Error(json.message || '上传失败')
  }

  log.info('上传完成', { targetPath })
}

/**
 * 批量更新 workspace 文本文件
 * POST /api/allocator/filesystem.workspace.updateText
 */
export async function updateWorkspaceText(
  instanceId: string,
  files: Record<string, string>,
): Promise<void> {
  await allocatorPost('filesystem.workspace.updateText', {
    id: instanceId,
    files,
  })
}

/**
 * 读取 workspace 二进制文件
 * POST /api/allocator/filesystem.workspace.readBinary
 * 返回 ArrayBuffer，用于 PDF / DOCX / XLSX / 图片等二进制格式
 */
export async function readWorkspaceBinary(
  instanceId: string,
  path: string,
): Promise<ArrayBuffer> {
  const res = await fetch(`${ALLOCATOR_BASE_URL}/filesystem.workspace.readBinary`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: instanceId, path }),
  })

  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || `allocator 请求失败 (${res.status})`)
  }

  return res.arrayBuffer()
}

// ── Home 文件系统（用户维度，id = userId） ──

/**
 * 读取 home 目录
 * POST /api/allocator/filesystem.home.readdir
 */
export function readHomeDir(
  userId: string,
  path: string,
  recursive = false,
): Promise<AllocatorDirEntry[]> {
  const key = `home:readdir:${userId}:${path}:${recursive}`
  return deduped(key, () =>
    allocatorPost<AllocatorDirEntry[]>('filesystem.home.readdir', {
      id: userId,
      path,
      recursive,
    }),
  )
}

/**
 * 读取 home 文件内容
 * POST /api/allocator/filesystem.home.readFile
 */
export async function readHomeFile(
  userId: string,
  path: string,
): Promise<string> {
  return allocatorPost<string>('filesystem.home.readFile', {
    id: userId,
    path,
  })
}

/**
 * 上传文件到 home
 * POST /api/allocator/filesystem.home.upload
 */
export async function uploadHomeFile(
  userId: string,
  path: string,
  content: string,
  deleteExisting = false,
): Promise<void> {
  await allocatorPost('filesystem.home.upload', {
    id: userId,
    path,
    content,
    deleteExisting,
  })
}

/**
 * 读取 home 二进制文件
 * POST /api/allocator/filesystem.home.readBinary
 */
export async function readHomeBinary(
  userId: string,
  path: string,
): Promise<ArrayBuffer> {
  const res = await fetch(`${ALLOCATOR_BASE_URL}/filesystem.home.readBinary`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: userId, path }),
  })

  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || `allocator 请求失败 (${res.status})`)
  }

  return res.arrayBuffer()
}

/**
 * 批量更新 home 文本文件
 * POST /api/allocator/filesystem.home.updateText
 */
export async function updateHomeText(
  userId: string,
  files: Record<string, string>,
): Promise<void> {
  await allocatorPost('filesystem.home.updateText', {
    id: userId,
    files,
  })
}
